import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
//import moment from 'moment';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  AdminPageHeader,
  ButtonStyles,
  //DatePicker,
  Input,
  InputTypes,
  MLPVersion,
  Theme,
  RadioButton,
  SearchComponent,
  DropDown,
  InputValidations,
} from '../../../../ui/components';
import { DropdownOption } from '../../../../ui/components/Form/SearchComponent/types/propTypes';
import { DropDownData } from '../../../../ui/components/Form/DropDown/types/propsType';
import { LoggerService, dateTimeToCustomFormat } from '../../../../ui/utils';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import EnrollmentService from '../../../../sdk/services/enrollmentService';
import userService from '../../../../sdk/services/userService';
import CampaignService from '../../../../sdk/services/campaignService';
import CoachService from '../../../../sdk/services/coachService';
import MDMSService from '../../../../sdk/services/mdmsService';
import PinService from '../../../../sdk/services/pinService';
import { useAPICall } from '../../../../hooks';
import styles from './index.module.scss';

export interface CoachEnrollmentData {
  campaignId: string;
  campaignName: string;
  validity: number;
  coachId: string;
  aboId: string;
  enrollmentType: string;
  name: string;
  pin: string;
  directLeader: string;
  directLeaderName?: string;
  directLeaderPin?: string;
  directLeaderEmail?: string;
  directLeaderPhone?: string;
  userName?: string;
  enrollmentDate: Date;
  endDate: Date;
  period: [Date, Date];
  status: string;
  remark: string;
  rollingMonth: number;
  campaignStatRecId: string;
  leaderPinCriteria?: number | undefined | null;
  gender?: string;
  race?: string;
  address: string;
  loaName?: string;
  accountType?: string;
  highestPinLevel?: string | number;
  pinLevel?: string | number;
  partyId: string | number;
}

export interface CampaignData {
  recId: number;
  campaignId: string;
  campaignName: string;
  campaignStart: string;
  campaignEnd: string;
  validity: number;
  status: string;
}

interface MasterPin {
  pinId: string;
  pinName: string;
}

export const AdminCoachEnrollmentEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { adminToast } = useAdminToast();
  const id = searchParams.get('');
  const [data, setData] = useState<CoachEnrollmentData | null>(null);
  const [campaignDropdownData, setCampaignDropdownData] = useState<DropDownData[]>([]);
  const [campaignData, setCampaignData] = useState<CampaignData[]>([]);
  const [coachData, setCoachData] = useState([]);
  //const [periodDisplay, setPeriodDisplay] = useState('');
  const [clickedFields, setClickedFields] = useState({
    campaignId: false,
    coachId: false,
    enrollmentDate: false,
  });
  const [invalid, setInvalid] = useState(true);
  const [invalidCoachId, setInvalidCoachId] = useState(false);
  const [coachList, setCoachList] = useState([]);
  const [filteredCoachList, setFilteredCoachList] = useState(coachList);
  const [masterPinList, setMasterPinList] = useState<MasterPin[]>([]);

  const apiCall = useAPICall();

  const isNew = id === 'new';

  useEffect(() => {
    validateInputs();
  }, [data, invalidCoachId]);

  useEffect(() => {
    (async () => {
      if (!isNew) {
        try {
          const response = await apiCall(EnrollmentService.getEnrollmentDetails, `${id}`);
          let result = response?.data?.data;
          if (result) {
            //result.rollingMonth = result.rollingMonth ? result.rollingMonth : result.validity;
            setData(result ?? {});
            // if (result?.enrollmentDate && result?.endDate) {
            //   setPeriodDisplay(
            //     dateTimeToCustomFormat(result?.enrollmentDate, 'MMM YYYY') +
            //       ' to ' +
            //       dateTimeToCustomFormat(result?.endDate, 'MMM YYYY'),
            //   );
            // }
          }
        } catch (error) {
          LoggerService.log('Failed to retrieve coach enrollment list', error);
        }
      } else {
        try {
          const res = await apiCall(CampaignService?.getCampaigns);
          const campaignData =
            res?.data?.data
              ?.filter(
                (i: DynamicObjectInterface) =>
                  i.status !== 'DELETED' &&
                  i.status !== 'CLOSED' &&
                  i.campaignType === 'CHALLENGE',
              )
              ?.sort(
                (a: DynamicObjectInterface, b: DynamicObjectInterface) =>
                  new Date(b.campaignStart).getTime() -
                  new Date(a.campaignStart).getTime(),
              ) ?? [];

          setCampaignData(campaignData);

          setCampaignDropdownData(
            campaignData.map((d: DynamicObjectInterface) => ({
              value: d.campaignId,
              title: `${d.campaignId} - ${d.campaignName}`,
            })),
          );
          
          //fetch master pin list
          let pinList = await apiCall(PinService?.getPinList);
          setMasterPinList(pinList?.data?.data);
        } catch (error) {
          LoggerService.log('Failed to retrieve campaign and coach list', error);
        }
      }
    })();
  }, [id, isNew]);

  const handleInputChange = async (name: string, value: DynamicObjectInterface) => {
    // if (name === 'enrollmentDate') {
    //   value = moment(value).startOf('month').toString();
    //   const toDate = moment(value)
    //     .add(data?.rollingMonth ? data.rollingMonth - 1 : 0, 'months')
    //     .endOf('month')
    //     .toString();
    //   setPeriodDisplay(dateTimeToCustomFormat(value, 'MMM YYYY') + ' to ' + dateTimeToCustomFormat(toDate, 'MMM YYYY'));
    //   setData(prevData => ({
    //     ...prevData!,
    //     endDate: new Date(toDate),
    //   }));
    // }

    if (name === 'campaignId') {
      const campaignRecId = campaignData?.find(r => r.campaignId === value)?.recId;
      let validity = 0;
      let leaderPinCriteria: null = null;
      let directLeader = data?.directLeader ?? '';
      let directLeaderName = data?.directLeaderName ?? '';
      let directLeaderPin = data?.directLeaderPin ?? '';
      let directLeaderPhone = data?.directLeaderPhone ?? '';
      let directLeaderEmail = data?.directLeaderEmail ?? '';
      let enrollmentDate = campaignData.find(r => r.campaignId === value)?.campaignStart ?? '';
      let endDate = campaignData.find(r => r.campaignId === value)?.campaignEnd ?? '';

      if (campaignRecId) {
        let res = await apiCall(CampaignService?.getCampaignDetail, campaignRecId?.toString());
        validity = res?.data?.data.activities?.coach?.validity?.value;
        leaderPinCriteria = res?.data?.data.activities?.leader?.pin?.[0]?.pinId;
      }

      if (data?.aboId && leaderPinCriteria) {
        const mdmsResult = await apiCall(MDMSService.getMDMSUserData, {
          aboNumber: data?.aboId,
          pinCriteria: leaderPinCriteria,
        });
        if (mdmsResult?.data?.data?.sponsor) {
          const findInnerSponsor = (sponsorObject: DynamicObjectInterface): DynamicObjectInterface => {
            if (sponsorObject && sponsorObject.sponsor) {
              return findInnerSponsor(sponsorObject.sponsor);
            }
            return sponsorObject;
          };
          const innermostSponsor = findInnerSponsor(mdmsResult?.data?.data?.sponsor);
          directLeader = innermostSponsor?.sponsorABONo;
          directLeaderName = innermostSponsor?.sponsorName;
          directLeaderPin = innermostSponsor?.pin
            ? (masterPinList?.find((i: MasterPin) => i.pinId === innermostSponsor?.pin)?.pinName ?? '')
            : '';
          directLeaderEmail = innermostSponsor?.sponsorEmail;
          directLeaderPhone = innermostSponsor?.sponsorPhone;
          // } else {
          //   adminToast({
          //     message: 'Failed to retrieve Direct Leader information. Please log out and log in again to register again.',
          //     theme: Theme.ERROR,
          //   });
        }
      }

      setData(prevData => ({
        ...prevData!,
        campaignName: campaignData.find(r => r.campaignId === value)?.campaignName ?? '',
        enrollmentDate: new Date(enrollmentDate),
        endDate: new Date(endDate),
        validity: validity,
        rollingMonth: validity,
        leaderPinCriteria: leaderPinCriteria,
        directLeader: directLeader,
        directLeaderName: directLeaderName,
        directLeaderPin: directLeaderPin,
        directLeaderEmail: directLeaderEmail,
        directLeaderPhone: directLeaderPhone,
      }));

      let coachList = await apiCall(CoachService?.getCoachList);

      setCoachData(coachList?.data?.data);

      const retrieveUser = coachList?.data?.data
      ?.filter((user: DynamicObjectInterface) => user.campaignId === value)
      .map((user: DynamicObjectInterface) => ({
        id: user.aboID,
        title: user.coachID,
        userId: user.userId,
      }));
      setCoachList(retrieveUser);

      // if (data?.enrollmentDate) {
      //   const toDate = moment(data?.enrollmentDate)
      //     .add(validity ? validity - 1 : 0, 'months')
      //     .endOf('month')
      //     .toString();
      //   setPeriodDisplay(
      //     dateTimeToCustomFormat(data?.enrollmentDate?.toString(), 'MMM YYYY') +
      //       ' to ' +
      //       dateTimeToCustomFormat(toDate, 'MMM YYYY'),
      //   );
      //   setData(prevData => ({
      //     ...prevData!,
      //     endDate: new Date(toDate),
      //   }));
      // }
    }

    // if (name === 'rollingMonth') {
    //   if (data?.enrollmentDate) {
    //     const toDate = moment(data?.enrollmentDate)
    //       .add(value ? value - 1 : 0, 'months')
    //       .endOf('month')
    //       .toString();
    //     setPeriodDisplay(
    //       dateTimeToCustomFormat(data?.enrollmentDate?.toString(), 'MMM YYYY') +
    //         ' to ' +
    //         dateTimeToCustomFormat(toDate, 'MMM YYYY'),
    //     );
    //     setData(prevData => ({
    //       ...prevData!,
    //       endDate: new Date(toDate),
    //     }));
    //   }
    // }

    setData(prevData => ({
      ...prevData!,
      [name]: value,
    }));

    setClickedFields(prevClickedFields => ({
      ...prevClickedFields,
      [name]: value === '',
    }));

    
  };

  const onSave = async () => {
    const payload = {
      campaignId: data?.campaignId,
      aboId: data?.aboId,
      enrollmentDate: data?.enrollmentDate
        ? dateTimeToCustomFormat(data?.enrollmentDate?.toString(), 'YYYY-MM-DD')
        : null,
      endDate: data?.endDate ? dateTimeToCustomFormat(data?.endDate?.toString(), 'YYYY-MM-DD') : null,
      enrollmentType: 'COACH',
      directLeader: data?.directLeader,
      remark: data?.remark,
      status: data?.status,
      coachId: data?.coachId,
      rollingMonth: data?.rollingMonth,
      campaignStatRecId: data?.campaignStatRecId ?? null,
      directLeaderName: data?.directLeaderName,
      directLeaderPin: data?.directLeaderPin
        ? masterPinList?.find((i: MasterPin) => i.pinName === data?.directLeaderPin)?.pinId
        : null,
      directLeaderPhone: data?.directLeaderPhone,
      directLeaderEmail: data?.directLeaderEmail,
      userName: data?.userName,
      gender: data?.gender,
      race: data?.race,
      address: data?.address,
      loaName: data?.loaName,
      accountType: data?.accountType,
      pinLevel: data?.pinLevel,
      highestPinLevel: data?.highestPinLevel,
      partyId: data?.partyId,
    };

    let result = isNew
      ? await apiCall(EnrollmentService.createEnrollment, payload)
      : await apiCall(EnrollmentService.updateEnrollment, `${id}`, payload);
    if (result.status === 200 && result?.data?.status) {
      navigate(`/admin/coach-enrollment-configuration/list`);
      adminToast({
        message: result?.data?.message,
        theme: Theme.SUCCESS,
      });
    } else {
      adminToast({
        message: result?.data?.message,
        theme: Theme.ERROR,
      });
    }
  };

  const buttonConfig = [
    {
      title: t('admin.coachEnrollmentEdit.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/coach-enrollment-configuration/list`);
      },
    },
    {
      title: t('admin.coachEnrollmentEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: invalid,
      onClick: onSave,
    },
  ];

  const validateInputs = () => {
    const { campaignId, aboId, enrollmentDate, pin } = data ?? {};
    const isAnyFieldEmpty = !campaignId || !aboId || !enrollmentDate || invalidCoachId || !pin;
    setInvalid(isAnyFieldEmpty);
  };

  const searchCoach = (input: string) => {
    // If the search input is empty, show the full coachList
    if (!input) {
      setFilteredCoachList(coachList);
    } else {
      const filteredUsers = coachList.filter((coach: DropdownOption) => {
        return coach.title.toLowerCase().includes(input.toLowerCase());
      });
      // Update the filteredCoachList state
      setFilteredCoachList(filteredUsers);
    }
  };

  const selectedUser = async (loginId: string, userId: string = '', coachId: string = '') => {
    setData(prevData => ({
      ...prevData!,
      coachId: '',
      name: '',
      pin: '',
      directLeader: '',
      directLeaderName: '',
      directLeaderPin: '',
      directLeaderPhone: '',
      directLeaderEmail: '',
      gender: '',
      race: '',
      address: '',
      loaName: '',
      accountType: '',
      highestPinLevel: '',
      partyId: '',
    }));

    let directLeader = '';
    let directLeaderName = '';
    let directLeaderPin = '';
    let directLeaderEmail = '';
    let directLeaderPhone = '';
    let pin = '';
    let highestPin='';
    let mdmsResult: DynamicObjectInterface = {};
    const result = await apiCall(userService.getUserDetails, `${userId}`);
    const coach = coachData?.find(
      (i: DynamicObjectInterface) => i.coachID?.toUpperCase() === coachId?.toUpperCase(),
    ) as DynamicObjectInterface;
    setInvalidCoachId(!coach?.coachID);
    if (result?.data?.data && coach?.coachID) {
      //if (data?.leaderPinCriteria) {
        mdmsResult = await apiCall(MDMSService.getMDMSUserData, {
          aboNumber: loginId,
          pinCriteria: data?.leaderPinCriteria || 0,
        });

        pin = mdmsResult?.data?.data.pin;
        highestPin = mdmsResult?.data?.data.highestPinLevel;

        if (data?.leaderPinCriteria && mdmsResult?.data?.data?.sponsor) {
          const findInnerSponsor = (sponsorObject: DynamicObjectInterface): DynamicObjectInterface => {
            if (sponsorObject && sponsorObject.sponsor) {
              return findInnerSponsor(sponsorObject.sponsor);
            }
            return sponsorObject;
          };
          const innermostSponsor = findInnerSponsor(mdmsResult?.data?.data?.sponsor);
          directLeader = innermostSponsor?.sponsorABONo;
          directLeaderName = innermostSponsor?.sponsorName;
          directLeaderPin = innermostSponsor?.pin
            ? (masterPinList?.find((i: MasterPin) => i.pinId === innermostSponsor?.pin)?.pinName ?? '')
            : '';
          directLeaderEmail = innermostSponsor?.sponsorEmail;
          directLeaderPhone = innermostSponsor?.sponsorPhone;
          // } else {
          //   adminToast({
          //     message: 'Failed to retrieve Direct Leader information. Please log out and log in again to register again.',
          //     theme: Theme.ERROR,
          //   });
        }
      //}

      setData(prevData => ({
        ...prevData!,
        userName: result?.data?.data?.name,
        coachId: coach?.coachID,
        name: coach?.name,
        pin: mdmsResult?.data?.data?.pin
          ? (masterPinList?.find((i: MasterPin) => i.pinId === mdmsResult?.data?.data?.pin)?.pinName ?? '')
          : '',
        directLeader: directLeader,
        directLeaderName: directLeaderName,
        directLeaderPin: directLeaderPin,
        directLeaderEmail: directLeaderEmail,
        directLeaderPhone: directLeaderPhone,
        gender: result?.data?.data?.gender,
        race: result?.data?.data?.race,
        address: result?.data?.data?.address,
        loaName: result?.data?.data?.loaName,
        accountType: result?.data?.data?.accountType,
        highestPinLevel: highestPin,
        pinLevel: pin,
        partyId: result?.data?.data?.partyId,
      }));
    }
  };

  return (
    <div>
      <AdminPageHeader
        title={isNew ? t('admin.coachEnrollmentEdit.newTitle') : t('admin.coachEnrollmentEdit.editTitle')}
        button={buttonConfig}
      />
      <div className={styles['edit-coachEnrollment-form-container']}>
        <div className={styles['input-container']}>
          <DropDown
            name="campaignId"
            dropdownLabel={`${t('admin.coachEnrollmentMaster.campaignId')}*`}
            disabledFilter={false}
            mlpVersion={MLPVersion.ONE}
            value={data?.campaignId}
            data={campaignDropdownData}
            configData={{
              label: 'title',
              onSelect: (selectedOption: DropDownData) => {
                handleInputChange('campaignId', selectedOption.value);
              },
            }}
            disabled={!isNew}
          />
        </div>
        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'campaignName'}
            label={`${t('admin.coachEnrollmentMaster.campaignName')}`}
            value={data?.campaignName}
            mlpVersion={MLPVersion.ONE}
            disabled={true}
          />
        </div>
        {/* <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.NUMBER}
            name={'validity'}
            label={`${t('admin.coachEnrollmentMaster.rollingMonth')}`}
            value={data?.validity}
            mlpVersion={MLPVersion.ONE}
            disabled={true}
          />
        </div> */}
      </div>
      <hr className={styles['horizontal-line']} />
      <div className={styles['row-container']}>
        <div className={styles['edit-coachEnrollment-form-container']}>
          <div className={styles['input-container']}>
            <SearchComponent
              status={InputValidations.DEFAULT}
              label={`${t('admin.coachEnrollmentMaster.coachId')}*`}
              placeholder={t('admin.teams.searchCoach')}
              initialSearchText={data?.coachId}
              labelChoice={'title'}
              dropdownOptions={filteredCoachList}
              mlpVersion={MLPVersion.TWO}
              onSearch={searchCoach}
              theme={clickedFields.coachId || invalidCoachId ? Theme.ERROR : Theme.BLACK}
              message={
                clickedFields.coachId
                  ? `${t('admin.coachEnrollmentMaster.requiredCoachId')}`
                  : invalidCoachId
                    ? 'Coach not found.'
                    : ''
              }
              onClear={() => {
                searchCoach('');
                selectedUser('');
                handleInputChange('aboId', '');
              }}
              onSelect={e => {
                selectedUser(e.id, e.userId, e.title);
                handleInputChange('aboId', e.id);
              }}
              disabled={!isNew}
            />
          </div>
        </div>
        <div className={styles['edit-coachEnrollment-form-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'aboId'}
            label={`${t('admin.coachEnrollmentMaster.aboId')}`}
            value={data?.aboId}
            mlpVersion={MLPVersion.ONE}
            disabled={true}
          />
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'name'}
            label={`${t('admin.coachEnrollmentMaster.coachName')}`}
            value={data?.name}
            mlpVersion={MLPVersion.ONE}
            disabled={true}
          />
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'pin'}
            label={`${t('admin.coachEnrollmentMaster.coachPin')}*`}
            value={data?.pin}
            mlpVersion={MLPVersion.ONE}
            disabled={true}
          />
        </div>
        <div className={styles['edit-coachEnrollment-form-container']}>
          <div className={styles['input-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'directLeader'}
              label={`${t('admin.coachEnrollmentMaster.directLeader')}`}
              value={data?.directLeader}
              mlpVersion={MLPVersion.ONE}
              disabled={true}
            />
          </div>
          <div className={styles['input-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'directLeaderName'}
              label={`${t('admin.coachEnrollmentMaster.directLeaderName')}`}
              value={data?.directLeaderName}
              mlpVersion={MLPVersion.ONE}
              disabled={true}
            />
          </div>
          <div className={styles['input-container']}>
            <Input
              key={id}
              type={InputTypes.TEXT}
              name={'directLeaderPin'}
              label={`${t('admin.coachEnrollmentMaster.directLeaderPin')}`}
              value={data?.directLeaderPin}
              mlpVersion={MLPVersion.ONE}
              disabled={true}
            />
          </div>
        </div>
      </div>
      <hr className={styles['horizontal-line']} />
      <div className={styles['edit-coachEnrollment-form-container']}>
        {/* <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.NUMBER}
            name={'rollingMonth'}
            label={`${t('admin.coachEnrollmentMaster.enrollmentRollingMonth')}`}
            value={data?.rollingMonth}
            onChange={e => {
              handleInputChange('rollingMonth', e.target.value);
            }}
            mlpVersion={MLPVersion.ONE}
          />
        </div> */}
        {/* <div className={styles['input-container']}>
          <DatePicker
            data-testid={'test-date-picker-single'}
            calendarHeading={`${t('admin.coachEnrollmentMaster.enrollmentDate')}`}
            inputContainerHeight="36px"
            value={data?.enrollmentDate ? new Date(data?.enrollmentDate) : undefined}
            inputLabel={`${t('admin.coachEnrollmentMaster.enrollmentDate')}*`}
            onChange={date => {
              handleInputChange('enrollmentDate', date);
            }}
            showDateContainer={false}
            displayText={
              data?.enrollmentDate ? dateTimeToCustomFormat(data?.enrollmentDate?.toString(), 'MMM YYYY') : ''
            }
          />
        </div> */}
        {/* <div className={styles['input-container']}>
          <DatePicker
            data-testid={'test-date-picker-single'}
            calendarHeading={`${t('admin.coachEnrollmentMaster.period')}`}
            inputContainerHeight="36px"
            inputLabel={`${t('admin.coachEnrollmentMaster.period')}`}
            selectRange
            displayText={periodDisplay}
            disabled
          />
        </div> */}
        <div className={styles['input-container']}>
          <Input
            key={id}
            type={InputTypes.TEXT}
            name={'remark'}
            label={`${t('admin.coachEnrollmentMaster.remark')}`}
            value={data?.remark}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChange('remark', e.target.value);
            }}
          />
        </div>
        {!isNew ? (
          <div className={styles['input-container']}>
            <div className={styles['input']}>
              <div className={styles['input-label']}>{`${t('admin.coachEnrollmentMaster.status')}`}</div>
              <div className={styles['radio-button-container']}>
                <RadioButton
                  label="Active"
                  checked={data?.status === 'ACTIVE'}
                  onClick={() => {
                    handleInputChange('status', 'ACTIVE');
                  }}
                />
                <RadioButton
                  label="Inactive"
                  checked={data?.status === 'INACTIVE'}
                  onClick={() => {
                    handleInputChange('status', 'INACTIVE');
                  }}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
